/**
 * IndexedDB-backed cache for real-account API responses.
 *
 * Goal: instant render on refresh + survive re-login (demo or real),
 * without hammering the WHB API or the user's bandwidth. Three of the
 * endpoints we rely on — `/broker-accounts`, `/broker-accounts/{id}`,
 * `/broker-accounts/{id}/financial-activities`, and `/trading-history`
 * — have no server-side `since=` filter, so every refresh re-downloads
 * the whole payload. That's acceptable when the cache is populated
 * (UI paints from cache in <10ms, network refresh runs in parallel),
 * but without a cache the first paint on every navigation feels slow.
 *
 * Storage model
 * -------------
 *   DB:     `whb`
 *   Store:  `entries` (keyPath: `key`)
 *   Shape:  { key: string, value: unknown, updatedAt: number }
 *
 * Key scheme
 * ----------
 *   `${kind}:${scopeId}`   — e.g.  `activities:b87effeb-…`
 *                                  `detail:b87effeb-…`
 *                                  `account-list:root`
 *                                  `orders:b87effeb-…`
 *
 * Cleared on signOut via {@link cacheClear} to avoid bleeding state
 * from one signed-in user to another when the same browser is shared.
 *
 * Everything is best-effort. Failures fall back to "no cache" so the
 * caller just fires the network request — we never throw out of the
 * read/write helpers.
 */

const DB_NAME = "whb";
const DB_VERSION = 1;
const STORE = "entries";

let dbPromise: Promise<IDBDatabase | null> | null = null;

/** Open (or create) the DB. Memoises the open promise so concurrent
 *  callers share a single connection. Returns `null` on any failure
 *  (private-mode Safari, storage quota, blocked upgrade) — callers
 *  already tolerate a null. */
function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

interface CacheEntry<T> {
  key: string;
  value: T;
  updatedAt: number;
}

/**
 * Read a cache entry. Returns `null` if not found, expired (controlled
 * by the caller's `maxAgeMs` param), or if IndexedDB is unavailable.
 *
 * The caller decides freshness — this helper only stores a wall-clock
 * `updatedAt` alongside each value.
 */
export async function cacheGet<T>(
  key: string,
  opts: { maxAgeMs?: number } = {}
): Promise<{ value: T; updatedAt: number } | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const entry = req.result as CacheEntry<T> | undefined;
        if (!entry) {
          resolve(null);
          return;
        }
        if (
          typeof opts.maxAgeMs === "number" &&
          Date.now() - entry.updatedAt > opts.maxAgeMs
        ) {
          resolve(null);
          return;
        }
        resolve({ value: entry.value, updatedAt: entry.updatedAt });
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Write (or overwrite) a cache entry. Stamped with `Date.now()`. Best-
 * effort — silently no-ops on failure.
 */
export async function cacheSet<T>(key: string, value: T): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put({ key, value, updatedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Delete a single cache entry by key. Best-effort — silently no-ops
 * on failure (db unavailable, key missing). Used by the archived-bots
 * lazy purge to evict bot-detail rows older than the retention window.
 */
export async function cacheDelete(key: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * List every cache entry whose key starts with `prefix`, returning
 * `{ key, value, updatedAt }` triples. Used by features that maintain
 * implicit registries via key prefixes (e.g. archived bots scan all
 * `bot-detail:*` entries to find ids no longer in the active list).
 *
 * Cursor-walks the store so we don't have to load every key into memory
 * up front. Returns an empty list on any error.
 */
export async function cacheListByPrefix<T = unknown>(
  prefix: string
): Promise<Array<{ key: string; value: T; updatedAt: number }>> {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    const results: Array<{ key: string; value: T; updatedAt: number }> = [];
    try {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      // Range from "{prefix}" up to (but not including) the next char's
      // namespace — avoids walking the entire store. The `￿`
      // sentinel is the highest UCS-2 code point so any string starting
      // with `prefix` sorts before `prefix + "￿"`.
      const range = IDBKeyRange.bound(prefix, prefix + "￿", false, true);
      const cursorReq = store.openCursor(range);
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) return;
        const entry = cursor.value as
          | { key: string; value: T; updatedAt: number }
          | undefined;
        if (entry) results.push(entry);
        cursor.continue();
      };
      cursorReq.onerror = () => resolve(results);
      tx.oncomplete = () => resolve(results);
      tx.onerror = () => resolve(results);
      tx.onabort = () => resolve(results);
    } catch {
      resolve(results);
    }
  });
}

/**
 * Merge an array payload into the cached array by `idKey`. Existing
 * rows with matching ids are replaced (latest-wins); new rows are
 * appended. The resulting array is written back and returned. If
 * no entry exists yet, `incoming` becomes the whole cache.
 *
 * Use this for `financial-activities` and `trading-history` — the
 * server returns the full array every time, but the cache's job is
 * to glue together the few-rows delta without losing ids we saw
 * earlier (defensive against a future server change that starts
 * pruning history).
 */
export async function cacheMergeById<T extends { id: string }>(
  key: string,
  incoming: readonly T[]
): Promise<T[]> {
  const existing = (await cacheGet<T[]>(key))?.value ?? [];
  const byId = new Map<string, T>();
  for (const row of existing) byId.set(row.id, row);
  for (const row of incoming) byId.set(row.id, row);
  const merged = Array.from(byId.values());
  await cacheSet(key, merged);
  return merged;
}

/** Key prefixes that are explicitly PRESERVED across signOut. Used by
 *  {@link cacheClear} to skip long-lived user state that shouldn't
 *  evaporate just because the user logged out and back in. */
const CLEAR_EXEMPT_PREFIXES = [
  // Reconciliation/correction orders. Per-account, scoped by accountId,
  // so they're safe to keep even if a different user signs in next —
  // their account ids won't collide. See `whb-corrections.ts`.
  "corrections:",
];

/**
 * Nuke every cache entry EXCEPT those under `CLEAR_EXEMPT_PREFIXES`.
 * Called from the auth store on signOut so a different user signing
 * in next doesn't see the previous user's real-account data.
 */
export async function cacheClear(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      // Walk every entry; delete only those not matching an exempt
      // prefix. Using a cursor so we don't have to load everything
      // into memory first.
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) return;
        const key = String(cursor.key ?? "");
        const keep = CLEAR_EXEMPT_PREFIXES.some((p) => key.startsWith(p));
        if (!keep) cursor.delete();
        cursor.continue();
      };
      cursorReq.onerror = () => resolve();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Convenience key builders — mirror the kinds used across the hook
 * layer. Putting them here keeps the scheme in one place so a rename
 * doesn't need to be echoed across five call sites.
 */
export const cacheKey = {
  accountList: () => "account-list:root",
  detail: (accountId: string) => `detail:${accountId}`,
  activities: (accountId: string) => `activities:${accountId}`,
  orders: (accountId: string) => `orders:${accountId}`,
  /** Per-account "missed" orders — manual fills in Alpaca that bypassed
   *  WHB's bot pipeline (e.g. user closed positions directly in Alpaca
   *  after deleting a bot). Sourced from
   *  `/broker-accounts/{id}/orders?from=…&to=…`. Only the unlinked rows
   *  (tradingHistoryRecordId === null) are consumed; they're stored
   *  here so cache-first paint surfaces them before the network round
   *  trip. Sergey 2026-04-30. */
  missedOrders: (accountId: string) => `missed-orders:${accountId}`,
  botsList: () => "bots-list:root",
  /** Per-bot detail from `/bots/{id}` — carries the bot's own
   *  `portfolio` block plus a 700+ row `states[]` (daily cash + stocks
   *  + margin) and 300+ row `dailyProfits[]`. Source for per-bot
   *  weekly/today deltas (recon update 2026-04-26). */
  botDetail: (botId: string) => `bot-detail:${botId}`,
  /** Per-bot full trading history — `/trading-history` filtered by
   *  botId, paginated to a single big page. **Critical**: the
   *  `placedOrders[]` embedded in `/bots/{id}` only carries open-lot
   *  buys for currently-held positions (verified 2026-04-26: Live E
   *  showed 30 entries vs the real 483 trades). Reconstruction MUST
   *  use this endpoint to see sells too, otherwise positions never
   *  close and equity never recovers from peaks. */
  botOrders: (botId: string) => `bot-orders:${botId}`,
  /** Tiny persistent registry of every bot we've ever seen in the
   *  user's /bots list — `Record<botId, { name, lastSeenIso }>`.
   *  Used by the "Archived bots" surface to recover the bot's name
   *  after deletion (the API drops the row entirely). Updated as a
   *  side-effect from `useRealBots` whenever a fresh /bots fetch
   *  lands. Sergey 2026-04-30. */
  botRegistry: () => "bot-registry:root",
  /** Per-archived-bot user-supplied allocation override events.
   *  Stored as `{ events: AllocationEvent[] }` where each event is
   *  `{ date: yyyy-mm-dd, deltaUsd: number }` — positive deltaUsd
   *  is a deposit (bumped allocation), negative is a withdrawal
   *  (allocation reduction). Used by `useArchivedBots` to feed
   *  mid-stream cashflows into `reconstructBotEquityCurve`, so the
   *  bot's lifetime curve reflects ND(t) accurately even when ND
   *  changed over time. Without these, the reconstruction assumes
   *  a single ND = peakSpent for the bot's whole lifetime — which
   *  matches the "no margin for bots" rule but misses user-driven
   *  allocation changes (e.g. Sergey reduced bot 070403cf from
   *  ~$23,300 to $18,980 on 2026-02-18). Sergey 2026-04-30. */
  archivedBotOverrides: (botId: string) =>
    `archived-bot-overrides:${botId}`,
  /** Per-bot reconstructed equity curve + computed metrics. Stored
   *  alongside its inputs' fingerprint so a new fill flips the key
   *  and forces a recompute. Subsequent loads paint instantly from
   *  this cache. */
  botReconstruction: (botId: string, fingerprint: string) =>
    `bot-recon:${botId}:${fingerprint}`,
  /** Real SPY daily history — shared across ALL accounts and all
   *  sessions (including demo — see `useRealSpyPriceSeries`), so it
   *  lives under a flat key with no per-user namespace. */
  spySeries: () => "market:spy:v1",
  /** Historical quotes per ticker — shared across accounts/users.
   *  Keyed by ticker so a new account that trades some of the same
   *  tickers as a prior account reuses the cache. */
  tickerQuotes: (ticker: string) => `market:quote:${ticker.toUpperCase()}`,
  /** Reconstructed per-account daily equity curve. Invalidated by a
   *  hash of the last order id + last activity id — any new trade or
   *  activity flips the key and forces a recompute. */
  equityReconstruction: (accountId: string, fingerprint: string) =>
    `reconstruction:${accountId}:${fingerprint}`,
  /** Persistent per-account reconciliation orders (Order[] with
   *  orderType="correction"). These close gaps in WHB's
   *  /trading-history feed — see `src/lib/whb-corrections.ts`.
   *  Survives sign-out by design; keyed by accountId so user A's
   *  corrections never leak into user B's reconstruction. */
  corrections: (accountId: string) => `corrections:${accountId}`,
} as const;
